import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebaseClient';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import { toast } from 'sonner';
import DiscordOnboardingTour from './DiscordOnboardingTour';

/**
 * Discord Connection Panel Component
 * 
 * Allows users to connect/disconnect their Discord account
 * and manage Discord notification settings
 */
const DiscordConnectionPanel = ({ isOpen, onClose }) => {
  const { user, profile } = useAuth();
  const [discordUsername, setDiscordUsername] = useState(profile?.discord_username || '');
  const [notifications, setNotifications] = useState(profile?.discord_notifications || false);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showTour, setShowTour] = useState(false);
  
  // Local state to track connection status for immediate UI updates
  const [localConnectionStatus, setLocalConnectionStatus] = useState(null);

  // Update local state when profile changes or component opens
  useEffect(() => {
    if (isOpen && profile) {
      setDiscordUsername(profile.discord_username || '');
      setNotifications(profile.discord_notifications || false);
      setLocalConnectionStatus(null); // Reset local override
      
      // Auto-launch tour for first-time users (no Discord username set)
      if (!profile.discord_username && !localStorage.getItem(`discord-tour-seen-${user.uid}`)) {
        setShowTour(true);
        localStorage.setItem(`discord-tour-seen-${user.uid}`, 'true');
      }
    }
  }, [isOpen, profile, user.uid]);

  // Determine connection status - use local state if available, otherwise profile
  const isConnected = localConnectionStatus !== null 
    ? localConnectionStatus 
    : profile?.discord_username && profile.discord_username.length > 0;

  // Validate Discord username format
  const validateDiscordUsername = (username) => {
    if (!username.trim()) return true; // Allow empty for disconnection
    
    const trimmed = username.trim();
    
    // New Discord format: just username (2-32 characters, letters, numbers, periods, underscores)
    const newFormatRegex = /^[a-zA-Z0-9._]{2,32}$/;
    
    // Legacy format: username#discriminator 
    const legacyFormatRegex = /^.{1,32}#\d{4}$/;
    
    // Allow either format
    return newFormatRegex.test(trimmed) || legacyFormatRegex.test(trimmed);
  };

  const handleConnect = async () => {
    if (!validateDiscordUsername(discordUsername)) {
      toast.error('Please enter a valid Discord username (e.g., john.doe or username#1234)', {
        duration: 5000,
        position: 'top-center'
      });
      return;
    }

    setLoading(true);
    
    try {
      const userProfileRef = doc(db, 'profiles', user.uid);
      const updateData = {
        discord_username: discordUsername.trim(),
        discord_notifications: notifications,
        discord_connected_at: discordUsername.trim() ? new Date() : null,
        discord_id: '' // Will be populated when bot connects
      };

      await updateDoc(userProfileRef, updateData);
      
      // Update local connection status immediately for UI feedback
      const newConnectionStatus = discordUsername.trim().length > 0;
      setLocalConnectionStatus(newConnectionStatus);
      
      toast.success(
        newConnectionStatus
          ? '✅ Discord account connected! You\'ll receive notifications via DM.'
          : '❌ Discord account disconnected.',
        {
          duration: 4000,
          position: 'top-center'
        }
      );
      
      onClose();
    } catch (error) {
      console.error('Error updating Discord connection:', error);
      toast.error('Failed to update Discord connection', {
        duration: 5000,
        position: 'top-center'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!isConnected) {
      toast.error('Please connect your Discord account first', {
        duration: 4000,
        position: 'top-center'
      });
      return;
    }

    setTesting(true);
    
    try {
      const testDiscordConnection = httpsCallable(functions, 'testDiscordConnection');
      const result = await testDiscordConnection();
      
      if (result.data.success) {
        toast.success('🧪 Test message sent! Check your Discord DMs.', {
          duration: 6000,
          position: 'top-center',
          style: {
            zIndex: 999999,
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
          }
        });
      } else {
        toast.error(`Failed to send test message: ${result.data.error || 'Unknown error'}`, {
          duration: 6000,
          position: 'top-center',
          style: {
            zIndex: 999999,
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
          }
        });
      }
    } catch (error) {
      console.error('Error testing Discord connection:', error);
      toast.error('Failed to send test message. Make sure your Discord username is correct and try again after deployment.', {
        duration: 6000,
        position: 'top-center',
        style: {
          zIndex: 999999,
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
        }
      });
    } finally {
      setTesting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <AnimatePresence mode="wait">
        {isOpen && (
          <motion.div
            key="discord-connection-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              key="discord-modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 w-full max-w-md p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#7289DA]/20 rounded-lg flex items-center justify-center">
                <Icon name="MessageCircle" size={20} className="text-[#7289DA]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Discord Integration</h2>
                <p className="text-white/60 text-sm">Get notifications via Discord DMs</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Connection Status */}
          <div className="mb-6 p-4 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-400' : 'bg-gray-400'}`} />
              <div>
                <p className="text-white font-medium">
                  {isConnected ? 'Connected' : 'Not Connected'}
                </p>
                {isConnected && (
                  <p className="text-white/60 text-sm">{profile.discord_username}</p>
                )}
              </div>
            </div>
          </div>

          {/* Discord Username Input */}
          <div className="mb-4">
            <label className="block text-white/80 text-sm font-medium mb-2">
              Discord Username
            </label>
            <input
              type="text"
              value={discordUsername}
              onChange={(e) => setDiscordUsername(e.target.value)}
              placeholder="john.doe or username#1234"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-[#7289DA] focus:outline-none focus:ring-2 focus:ring-[#7289DA]/20"
            />
            <div className="flex items-center justify-between mt-2">
              <p className="text-white/40 text-xs">
                Enter your Discord username (new format: john.doe or legacy: username#1234)
              </p>
              <button
                onClick={() => setShowTour(true)}
                className="text-[#7289DA] hover:text-[#677BC4] text-xs font-medium flex items-center space-x-1 transition-colors"
              >
                <Icon name="HelpCircle" size={14} />
                <span>Need help?</span>
              </button>
            </div>
          </div>

          {/* Notification Settings */}
          <div className="mb-6">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notifications}
                onChange={(e) => setNotifications(e.target.checked)}
                className="w-4 h-4 text-[#7289DA] bg-white/10 border-white/20 rounded focus:ring-[#7289DA]/20 focus:ring-2"
              />
              <div>
                <span className="text-white/80 text-sm font-medium">Enable Discord Notifications</span>
                <p className="text-white/50 text-xs">Receive message notifications via Discord DMs</p>
              </div>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handleConnect}
              disabled={loading}
              className="flex-1 bg-[#7289DA] hover:bg-[#677BC4] text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <Icon name="Loader2" size={16} className="animate-spin mr-2" />
              ) : (
                <Icon name={isConnected ? "RefreshCw" : "Link"} size={16} className="mr-2" />
              )}
              {isConnected ? 'Update' : 'Connect'}
            </button>
            
            {isConnected && (
              <button
                onClick={handleTestConnection}
                disabled={testing}
                className="bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {testing ? (
                  <Icon name="Loader2" size={16} className="animate-spin" />
                ) : (
                  <Icon name="Zap" size={16} />
                )}
              </button>
            )}
          </div>

          {/* Disconnect Option */}
          {isConnected && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <button
                onClick={() => {
                  setDiscordUsername('');
                  setNotifications(false);
                }}
                className="w-full text-red-400 hover:text-red-300 text-sm font-medium py-2 transition-colors"
              >
                Disconnect Discord
              </button>
            </div>
          )}

          {/* Info Section */}
          <div className="mt-6 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
            <h4 className="text-blue-300 font-medium text-sm mb-2">How it works:</h4>
            <ul className="text-blue-200/80 text-xs space-y-1">
              <li>• Get instant notifications when you receive new messages</li>
              <li>• Reply directly in Discord DMs - messages sync to our app</li>
              <li>• Choose which conversations to get notified about</li>
              <li>• Your Discord stays private - we only send you DMs</li>
            </ul>
          </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Discord Onboarding Tour - Separate AnimatePresence */}
      <AnimatePresence mode="wait">
        {showTour && (
          <DiscordOnboardingTour
            key="discord-onboarding-tour"
            isOpen={showTour}
            onClose={() => setShowTour(false)}
            onComplete={() => {
              // Tour completed - user can now proceed with setup
              setShowTour(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default DiscordConnectionPanel;